import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeftRight, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CurrencyConverter() {
  const [currencies, setCurrencies] = useState([]);
  const [amount, setAmount] = useState("100");
  const [fromCurrency, setFromCurrency] = useState("INR");
  const [toCurrency, setToCurrency] = useState("USD");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rates, setRates] = useState(null);

  useEffect(() => {
    fetchCurrencies();
    fetchRates();
  }, []);

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/currencies`);
      setCurrencies(response.data.currencies);
    } catch (error) {
      console.error("Error fetching currencies:", error);
      toast.error("Failed to load currencies");
    }
  };

  const fetchRates = async () => {
    try {
      const response = await axios.get(`${API}/forex/rates?base=INR`);
      setRates(response.data.rates);
    } catch (error) {
      console.error("Error fetching rates:", error);
    }
  };

  const handleConvert = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/forex/convert?amount=${amount}&from_currency=${fromCurrency}&to_currency=${toCurrency}`
      );
      setResult(response.data);
    } catch (error) {
      console.error("Error converting currency:", error);
      toast.error("Failed to convert currency");
    } finally {
      setLoading(false);
    }
  };

  const handleSwap = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    setResult(null);
  };

  const getCurrencySymbol = (code) => {
    const currency = currencies.find((c) => c.code === code);
    return currency?.symbol || code;
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto animate-fade-in" data-testid="currency-converter-page">
      <div className="mb-8">
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-primary" data-testid="converter-title">
          Currency Converter
        </h1>
        <p className="text-base text-muted-foreground mt-2">Convert between different currencies using live exchange rates</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Converter Card */}
        <Card className="bg-white rounded-xl border border-border/60 shadow-sm lg:col-span-2" data-testid="converter-card">
          <CardHeader className="p-6 pb-4">
            <CardTitle className="text-2xl font-semibold text-primary">Convert Currency</CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <div className="space-y-6">
              <div>
                <Label htmlFor="amount">Amount</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="amount-input"
                  placeholder="100.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="mt-1 text-lg"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-end">
                <div>
                  <Label htmlFor="from">From</Label>
                  <Select value={fromCurrency} onValueChange={setFromCurrency}>
                    <SelectTrigger className="mt-1" data-testid="from-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((currency) => (
                        <SelectItem key={currency.code} value={currency.code} data-testid={`from-${currency.code}`}>
                          {currency.code} - {currency.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleSwap}
                  className="rounded-full transition-transform duration-200 hover:rotate-180"
                  data-testid="swap-button"
                >
                  <ArrowLeftRight className="w-4 h-4" />
                </Button>

                <div>
                  <Label htmlFor="to">To</Label>
                  <Select value={toCurrency} onValueChange={setToCurrency}>
                    <SelectTrigger className="mt-1" data-testid="to-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((currency) => (
                        <SelectItem key={currency.code} value={currency.code} data-testid={`to-${currency.code}`}>
                          {currency.code} - {currency.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                onClick={handleConvert}
                disabled={loading}
                className="w-full rounded-full font-medium transition-all duration-200 active:scale-95 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md"
                data-testid="convert-button"
              >
                {loading ? "Converting..." : "Convert"}
              </Button>

              {result && (
                <div className="mt-6 p-6 bg-muted rounded-xl animate-slide-up" data-testid="conversion-result">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-2">Converted Amount</p>
                    <p className="text-4xl font-bold text-primary mb-4">
                      {getCurrencySymbol(result.to)} {result.converted_amount.toLocaleString()}
                    </p>
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <span>
                        {getCurrencySymbol(result.from)} {result.amount.toLocaleString()} {result.from}
                      </span>
                      <span>=</span>
                      <span>
                        {getCurrencySymbol(result.to)} {result.converted_amount.toLocaleString()} {result.to}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Exchange Rate: 1 {result.from} = {result.rate.toFixed(4)} {result.to}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Live Rates Card */}
        <Card className="bg-white rounded-xl border border-border/60 shadow-sm" data-testid="live-rates-card">
          <CardHeader className="p-6 pb-4">
            <CardTitle className="text-xl font-semibold text-primary flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Live Rates (INR)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            {rates ? (
              <div className="space-y-3">
                {Object.entries(rates).slice(0, 8).map(([currency, rate]) => (
                  <div key={currency} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <span className="font-medium text-sm">{currency}</span>
                    <span className="text-sm text-muted-foreground">{rate?.toFixed(4) || 'N/A'}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">Loading rates...</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Supported Currencies */}
      <Card className="bg-white rounded-xl border border-border/60 shadow-sm mt-6" data-testid="supported-currencies-card">
        <CardHeader className="p-6 pb-4">
          <CardTitle className="text-xl font-semibold text-primary">Supported Currencies</CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {currencies.map((currency) => (
              <div key={currency.code} className="flex items-center gap-2 p-3 bg-muted rounded-lg" data-testid={`currency-${currency.code}`}>
                <span className="text-2xl">{currency.symbol}</span>
                <div>
                  <p className="font-semibold text-sm">{currency.code}</p>
                  <p className="text-xs text-muted-foreground">{currency.name}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}